import * as yup from 'yup';
import { ISODateRegEx } from './dates';
yup.setLocale({
    mixed: {
        required: 'required'
    }
});
export const MAX_CHECK_INS = 5;
const alertEvents = ['bizMessageAlert', 'bizCustomerAlert', 'connMessageAlert'];
export const AlertEventsSchema = yup.string().oneOf(alertEvents);
const reactions = ['read', 'smile', 'thumbsUp', 'thumbsDown', 'heart'];
export const ReactionSchema = yup.string().oneOf(reactions).required();
const statuses = ['active', 'deleted', 'pending', 'rejected'];
export const STATUS_ACTIVE = 'active';
export const StatusSchema = yup.string().oneOf(statuses).required();
export const FREQUENCIES = ['instant', '5 minutes', 'hour', 'day', 'week', 'never', 'badge', 'hide'];
export const FREQUENCY_NEVER = 'never';
export const FrequencySchema = yup.string().oneOf(FREQUENCIES).required();
export const FREQUENCY_TO_MINUTES = new Map([
    ['instant', 1],
    ['5 minutes', 5],
    ['hour', 60],
    ['day', 60 * 24],
    ['week', 60 * 24 * 7]
]);
export const DEFAULT_BIZ_CUSTOMER_ALERT_FREQUENCY = '5 minutes';
export const DEFAULT_DIRECT_MESSAGE_FREQUENCY = '5 minutes';
export const DEFAULT_FREQUENCY = 'day';
export const SAVED_ACTIVATION_PREFIX = 'SAVED';
export const SAVED_ACTIVATION_REGEX = /SAVED[a-zA-Z0-9]+/;
//
// sub-types - These types are used as members of other types, so we declare them first.
//
export const BusinessProfileSchema = yup.object().shape({
    contactName: yup.string().required().default(''),
    businessName: yup.string().required().default(''),
    avatar: yup.string().defined().default(''),
    logoImageUrl: yup.string().defined().default(''),
    welcomeMessage: yup.string().required().default(''),
    publicActivationSecretId: yup.string().defined(),
    websiteUrl: yup.string().defined().default(''),
    hours: yup.string().defined().default(''),
    groups: yup.array().of(yup.object({
        id: yup.string().required(),
        priv: yup.boolean().defined(),
        name: yup.string().required(),
        color: yup.string().required()
    }).required()).defined()
});
//
// regular types
//
export const ActivationSchema = yup.object().shape({
    id: yup.string().required(),
    activationSecretId: yup.string().required(),
    userName: yup.string().notRequired(),
    businessProfile: BusinessProfileSchema.required()
});
export const ActivationCreateSchema = yup.object().shape({
    id: yup.string().matches(SAVED_ACTIVATION_REGEX).required(),
    activationSecretId: yup.string().required(),
    userName: yup.string().notRequired()
});
export const ActivationCodeSchema = yup.object({
    id: yup.string().required(),
    path: yup.string().defined()
});
export const ActivationRedeemCodeSchema = yup.object({
    code: yup.string().required()
});
export const ActivationSendSchema = yup.object().shape({
    email: yup.string().email().required().default('')
});
export const AlertSchema = yup.object().shape({
    userId: yup.string().required(),
    id: yup.string().required(),
    createdAt: yup.string().required(),
    alertType: yup.string().oneOf(alertEvents).required(),
    referencePath: yup.string().required(),
    notifyAt: yup.string().notRequired(),
    active: yup.boolean().required().default(false)
});
export const AlertPatchSchema = yup.object().shape({
    action: yup.string().oneOf(['clearNotify', 'delete']).required(),
    ids: yup.array().of(yup.string().required()).required()
});
export const BusinessCreateSchema = yup.object().shape({
    contactName: yup.string().required().default(''),
    businessName: yup.string().required().default(''),
    avatar: yup.string().default(''),
    logoS3ImageName: yup.string().default(''),
    welcomeMessage: yup.string().required().default(''),
    websiteUrl: yup.string().url().default(''),
    hours: yup.string().default('')
});
export const BusinessUpdateSchema = yup.object().shape({
    contactName: yup.string().required().default(''),
    businessName: yup.string().required().default(''),
    avatar: yup.string().default(''),
    logoS3ImageName: yup.string().default(''),
    welcomeMessage: yup.string().required().default(''),
    websiteUrl: yup.string().url().default(''),
    hours: yup.string().default(''),
    resetActiviationSecretId: yup.boolean().default(false)
});
// Businesses can only have one profile, but I think resthooks will be happier if it has an id, so use this for every
// the id of every row.
export const BUSINESS_STATIC_ID = '0';
export const BusinessSchema = yup.object().shape({
    id: yup.string().required(),
    createdAt: yup.string().required(),
    createdBy: yup.string().required(),
    contactName: yup.string().required().default(''),
    businessName: yup.string().required().default(''),
    avatar: yup.string().default(''),
    logoImageUrl: yup.string().notRequired(),
    logoS3ImageName: yup.string().notRequired(),
    welcomeMessage: yup.string().required().default(''),
    websiteUrl: yup.string().notRequired().default(''),
    hours: yup.string().notRequired().default(''),
    publicActivationSecretId: yup.string().notRequired()
});
export const ConnectionCreateSchema = yup.object().shape({
    activationSecretId: yup.string().required()
});
export const ConnectionUpdateSchema = yup.object().shape({
    notifications: yup.object({
        direct: yup.string().oneOf(FREQUENCIES).required(),
        other: yup.string().oneOf(FREQUENCIES).required(),
        groups: yup.array().of(yup.object({
            id: yup.string().required(),
            freq: yup.string().oneOf(FREQUENCIES).required()
        }).required()).defined()
    }).required()
});
export const ConnectionSchema = yup.object({
    id: yup.string().required(),
    createdAt: yup.string().required(),
    nickname: yup.string().required().default(''),
    avatar: yup.string().default(''),
    status: yup.string().oneOf(statuses).required(),
    sharedNotificationFrequency: yup.string().notRequired(),
    businessProfile: BusinessProfileSchema.required(),
    notifications: yup.object({
        direct: yup.string().oneOf(FREQUENCIES).required(),
        other: yup.string().oneOf(FREQUENCIES).required(),
        groups: yup.array().of(yup.object({
            id: yup.string().required(),
            freq: yup.string().oneOf(FREQUENCIES).required()
        }).required()).defined()
    }).notRequired()
});
export const ConnectionMessageCreateSchema = yup.object({
    threadId: yup.string().required(),
    // channelId is implicitily the "Me & the business " channgel: cstZ<customerId>
    text: yup.string().required().default('')
});
export const ConnectionMessageUpdateSchema = yup.object({
    text: yup.string().required().default('')
});
export const ConnectionMessageSchema = yup.object().shape({
    id: yup.string().required(),
    createdAt: yup.string().required(),
    threadId: yup.string().required(),
    channelId: yup.string().required(),
    groupIds: yup.array().of(yup.string().required()).default([]).defined(),
    createdById: yup.string().required(),
    text: yup.string().required(),
    imageUrl: yup.string().notRequired(),
    anonymousReactions: yup.array().of(yup.string().oneOf(reactions).required()).defined(),
    reactions: yup.array().of(yup.string().oneOf(reactions).required()).defined()
});
export const CustomerUpdateSchema = yup.object().shape({
    groupIds: yup.array().of(yup.string().required()).default([]),
    notes: yup.string()
});
export const CustomerSchema = yup.object().shape({
    businessId: yup.string().required(),
    id: yup.string().required(),
    createdAt: yup.string().required(),
    activatedAt: yup.string(),
    nickname: yup.string().required(),
    avatar: yup.string().notRequired(),
    checkIns: yup.array().of(yup.string().matches(ISODateRegEx).required()),
    status: yup.string().oneOf(statuses).required(),
    sharedNotificationFrequency: yup.string().notRequired(),
    groupIds: yup.array().of(yup.string().required()).defined(),
    referrer: yup.string(),
    notes: yup.string().max(2000)
});
export const GroupCreateSchema = yup.object().shape({
    priv: yup.boolean().defined().default(false),
    name: yup.string().required().default(''),
    color: yup.string().required().default('bg-blue')
});
export const GroupUpdateSchema = yup.object().shape({
    priv: yup.boolean().defined().default(false),
    name: yup.string().required().default(''),
    color: yup.string().required().default('bg-blue'),
    resetActiviationSecretId: yup.boolean().default(false)
});
export const GroupSchema = yup.object().shape({
    businessId: yup.string().required(),
    id: yup.string().required(),
    createdAt: yup.string().required(),
    priv: yup.boolean().defined().default(false),
    name: yup.string().required().default(''),
    color: yup.string().required().default('bg-blue'),
    activationSecretId: yup.string().required()
});
export const MessageCreateSchema = yup.object().shape({
    threadId: yup.string().notRequired(),
    channelId: yup.string().required(),
    groupIds: yup.array().of(yup.string().required()).default([]).defined(),
    text: yup.string().required().default(''),
    s3ImageName: yup.string().notRequired()
});
export const MessageUpdateSchema = yup.object().shape({
    text: yup.string().required().default(''),
    s3ImageName: yup.string().notRequired()
});
export const ReactionListSchema = yup.object().shape({
    read: yup.array().of(yup.string().required()),
    smile: yup.array().of(yup.string().required()),
    thumbsUp: yup.array().of(yup.string().required()),
    thumbsDown: yup.array().of(yup.string().required()),
    heart: yup.array().of(yup.string().required())
});
export const ReactionCountSchema = yup.object().shape({
    read: yup.number().notRequired(),
    smile: yup.number().notRequired(),
    thumbsUp: yup.number().notRequired(),
    thumbsDown: yup.number().notRequired(),
    heart: yup.number().notRequired()
});
export const MessageSchema = yup.object().shape({
    businessId: yup.string().required(),
    id: yup.string().required(),
    createdAt: yup.string().required(),
    threadId: yup.string().required(),
    channelId: yup.string().required(),
    groupIds: yup.array().of(yup.string().required()).default([]).defined(),
    createdById: yup.string().required(),
    text: yup.string().required(),
    imageUrl: yup.string().notRequired(),
    s3ImageName: yup.string().notRequired(),
    reactionList: ReactionListSchema.required(),
    anonymousReactionCount: ReactionCountSchema.required()
});
export const NotificationUnsubscribeSchema = yup.object({
    email: yup.string().required()
});
export const CognitoUserCreateSchema = yup.object({
    convertTokenUser: yup.boolean().notRequired().default(false),
    biz: yup.boolean().notRequired().default(false)
});
export const TokenUserCreateSchema = yup.object({
    nickname: yup.string().required().default(''),
    avatar: yup.string().notRequired(),
    email: yup.string().email().required().default('')
});
export const SendLoginTokenSchema = yup.object({
    email: yup.string().email('Must be an email address').required('Email is required').default(''),
    activationId: yup.string().notRequired()
});
export const SendLoginTokenResultSchema = yup.object({
    result: yup.string().oneOf(['sent', 'notfound', 'notauthorized']).required()
});
export const UserPatchSchema = yup.object().shape({
    nickname: yup.string().notRequired(),
    avatar: yup.string().notRequired(),
    bizCustomerAlertFrequency: yup.string().oneOf(FREQUENCIES),
    directMessageNotificationFrequency: yup.string().oneOf(FREQUENCIES),
    notificationFrequency: yup.string().oneOf(FREQUENCIES),
    sharedNotificationFrequency: yup.string().notRequired(),
    emailEnabled: yup.boolean().notRequired(),
    emailAlways: yup.boolean().notRequired(),
    pushEnabled: yup.boolean().notRequired(),
    timezoneOffset: yup.number().notRequired(),
    platform: yup.string().notRequired(),
    appVersion: yup.string().notRequired()
});
export const UserSchema = yup.object().shape({
    userId: yup.string().required(),
    createdAt: yup.string().required(),
    tokenUserSecret: yup.string().notRequired(),
    nickname: yup.string().notRequired().default(''),
    avatar: yup.string().notRequired().default(''),
    permissions: yup.array().of(yup.object({ name: yup.string().required(), path: yup.string().required() }).required()).default([]),
    bizCustomerAlertFrequency: yup.string().oneOf(FREQUENCIES),
    directMessageNotificationFrequency: yup.string().oneOf(FREQUENCIES),
    notificationFrequency: yup.string().oneOf(FREQUENCIES),
    sharedNotificationFrequency: yup.string().notRequired(),
    emailEnabled: yup.boolean().notRequired(),
    emailAlways: yup.boolean().notRequired(),
    maskedEmail: yup.string().notRequired(),
    pushEnabled: yup.boolean().default(false),
    timezoneOffset: yup.number().notRequired(),
    platform: yup.string().notRequired(),
    appVersion: yup.string().notRequired(),
    connectToSprancerPath: yup.string().notRequired()
});
export const UserPushDeviceSchema = yup.object({
    userId: yup.string().required(),
    token: yup.string().required(),
    createdAt: yup.string().required(),
    updatedAt: yup.string().required(),
    deviceName: yup.string().defined(),
    app: yup.string().required(),
    platform: yup.string().required()
});
export const UserPushDeviceCreateSchema = yup.object({
    token: yup.string().required(),
    deviceName: yup.string().defined(),
    app: yup.string().oneOf(['biz', 'cust']).notRequired().default('cust'),
    platform: yup.string().oneOf(['APN', 'FCM']).required()
});
export const UserPushDevicePatchSchema = yup.object({
    updatedAt: yup.string().oneOf(['now']).required()
});
//# sourceMappingURL=schemas.js.map